import { useQueryClient, useMutation } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { toaster } from '~/shared/utils/toaster';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { getOrgAccountsScopeValue } from '~/shared/utils/organizationUtils';

import {
  ConfigurationLevel,
  configurationQueryKey,
  configurationTreeQueryKey,
  updateConfiguration,
} from '../helpers/configurationApi';
import {
  formatFieldDisplayName,
  transformErrorMessage,
  MIN_SIZE_BYTES_DOUBLE_DISPLAY_FIELDS,
  isInstanceFamilyLevel,
  getEffectiveInstanceFamilyId,
  getInstanceFamilyErrorMessage,
  buildConfigurationScope,
} from '../helpers/utils';
import { parseConfigValue, formatConfigValue } from '~/settings/utils/configValueParser';

import { useRevertConfigurationMutation } from './useRevertConfigurationMutation';

function toApiFormatForComparison(fieldName: string, value: string): string {
  if ((MIN_SIZE_BYTES_DOUBLE_DISPLAY_FIELDS as readonly string[]).includes(fieldName)) {
    const parsed = parseConfigValue(value, 'size');
    return formatConfigValue(parsed.value / 2, parsed.unit, 'size');
  }
  return value;
}

import type { ConfigRecord } from '../types';

interface SelectedNode {
  id: string | null;
  name: string | null;
  level?: ConfigurationLevel;
  clusterId?: string;
  instanceId?: string;
  region?: string;
  accountId?: string;
}

export const useConfigurationActions = (selectedNode: SelectedNode, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();
  const accountsScope = getOrgAccountsScopeValue(accounts, activeTenantId);

  const revertMutation = useRevertConfigurationMutation({
    nodeId: selectedNode.id,
  });

  const updateMutation = useMutation({
    mutationFn: async (body: {
      configurations: Record<string, string>;
      level: ConfigurationLevel;
      clusterId?: string;
      instanceId?: string;
      accountId?: string;
      region: string;
    }) => {
      const result = await updateConfiguration(api, {
        configurations: body.configurations,
        level: body.level,
        clusterId: body.clusterId,
        instanceId: body.instanceId,
        accountId: body.accountId,
        region: body.region,
      });
      return { data: result, status: 200, statusText: 'OK' };
    },
    onSuccess: async (data, variables) => {
      if (selectedNode.id) {
        const queryKey = configurationQueryKey({
          nodeId: selectedNode.id,
          level: variables.level,
          clusterId: variables.clusterId,
          instanceId: variables.instanceId,
          region: variables.region,
          accountId: selectedNode.accountId,
        });

        const updatedAt = data.data.updated;
        if (updatedAt) {
          queryClient.setQueryData<ConfigRecord | null>(queryKey, (oldData) => {
            if (!oldData) {
              return null;
            }
            return {
              ...oldData,
              lastUpdated: updatedAt,
            };
          });
        }

        queryClient.invalidateQueries({
          queryKey,
          exact: true,
        });

        queryClient.invalidateQueries({
          queryKey: configurationTreeQueryKey(accounts ?? [], 'edited', accountsScope),
          exact: true,
        });

        queryClient.invalidateQueries({
          queryKey: ['configuration'],
          refetchType: 'none',
        });
      }
      onSuccess?.();
    },
    onError: (error: unknown) => {
      console.error('[updateMutation] onError - error:', error);
    },
  });

  const handleSave = async (
    _nodeId: string,
    fields: Partial<Record<string, string | number>>,
    originalValues: Record<string, string>
  ) => {
    const isInstanceLevel = isInstanceFamilyLevel(selectedNode.level);
    const effectiveInstanceFamilyId = getEffectiveInstanceFamilyId(
      selectedNode.level,
      selectedNode.id,
      selectedNode.instanceId
    );

    if (!selectedNode.level) {
      toaster({
        title: 'Error',
        message: 'Missing required parameters (level) for saving configuration.',
        variant: 'error',
      });
      return;
    }

    if (selectedNode.level === 'cluster' && !selectedNode.clusterId) {
      toaster({
        title: 'Error',
        message: 'clusterId is required for cluster level configurations.',
        variant: 'error',
      });
      return;
    }

    if (isInstanceLevel && !effectiveInstanceFamilyId) {
      toaster({
        title: 'Error',
        message: getInstanceFamilyErrorMessage(selectedNode.level),
        variant: 'error',
      });
      return;
    }

    const region = selectedNode.region || 'us-east-1';
    const configurations: Record<string, string> = {};

    Object.keys(originalValues).forEach((fieldName) => {
      const originalValue = originalValues[fieldName];
      const newValue = fields[fieldName];

      if (newValue === undefined || newValue === null || newValue === '') {
        return;
      }

      if (
        typeof newValue === 'object' &&
        newValue !== null &&
        !Array.isArray(newValue) &&
        typeof newValue !== 'string' &&
        typeof newValue !== 'number'
      ) {
        return;
      }

      const newValueStr = String(newValue);
      const originalValueForComparison = toApiFormatForComparison(fieldName, originalValue);

      if (newValueStr !== originalValueForComparison) {
        configurations[fieldName] = newValueStr;
      }
    });

    if (Object.keys(configurations).length === 0) {
      return;
    }

    const scope = buildConfigurationScope({
      level: selectedNode.level,
      accountId: selectedNode.accountId,
      clusterId: selectedNode.clusterId,
      nodeId: selectedNode.id,
      instanceId: selectedNode.instanceId,
    });

    const body = {
      configurations,
      level: selectedNode.level,
      region,
      ...scope,
    };

    try {
      await updateMutation.mutateAsync(body);

      toaster({
        title: 'Changes Saved',
        message: `Your changes to ${selectedNode.name} have been saved successfully.`,
        variant: 'success',
      });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err: any = error;
      let errorMessage =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        err?.message ??
        (error instanceof Error ? error.message : String(error));

      errorMessage = transformErrorMessage(errorMessage);
      toaster({
        title: 'Invalid data',
        message: errorMessage,
        variant: 'error',
      });
      throw error;
    }
  };

  const handleRevertField = async (_nodeId: string, fieldName: string) => {
    if (!selectedNode.level || !selectedNode.accountId) {
      throw new Error('Missing required parameters for revert');
    }

    const region = selectedNode.region || 'us-east-1';
    const scope = buildConfigurationScope({
      level: selectedNode.level,
      accountId: selectedNode.accountId,
      clusterId: selectedNode.clusterId,
      nodeId: selectedNode.id,
      instanceId: selectedNode.instanceId,
    });

    try {
      const response = await revertMutation.mutateAsync({
        level: selectedNode.level as ConfigurationLevel,
        keys: [fieldName],
        accountId: selectedNode.accountId,
        region,
        clusterId: scope.clusterId,
        instanceId: scope.instanceId,
      });

      const displayName = formatFieldDisplayName(fieldName);

      toaster({
        title: 'Default restored',
        message: `${displayName} has been successfully reverted to default.`,
        variant: 'success',
      });

      return response;
    } catch (error) {
      const displayName = formatFieldDisplayName(fieldName);

      toaster({
        title: 'Error',
        message: `Failed to revert field ${displayName}. Please try again.`,
        variant: 'error',
      });
      throw error;
    }
  };

  const handleRevertAll = async (_nodeId: string) => {
    if (!selectedNode.level || !selectedNode.accountId) {
      throw new Error('Missing required parameters for revert');
    }

    const region = selectedNode.region || 'us-east-1';
    const scope = buildConfigurationScope({
      level: selectedNode.level,
      accountId: selectedNode.accountId,
      clusterId: selectedNode.clusterId,
      nodeId: selectedNode.id,
      instanceId: selectedNode.instanceId,
    });

    try {
      const response = await revertMutation.mutateAsync({
        level: selectedNode.level as ConfigurationLevel,
        keys: ['*'],
        accountId: selectedNode.accountId,
        region,
        clusterId: scope.clusterId,
        instanceId: scope.instanceId,
      });

      toaster({
        title: 'Defaults restored',
        message: 'All configuration settings were reverted to their default values',
        variant: 'success',
      });

      return response;
    } catch (error) {
      toaster({
        title: 'Error',
        message: 'Failed to revert all fields. Please try again.',
        variant: 'error',
      });
      throw error;
    }
  };

  return {
    handleSave,
    handleRevertField,
    handleRevertAll,
  };
};
